import { useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { Subscription } from 'rxjs';
import { NotificationService } from 'services/notification-service';
import { PosthogEvent, PosthogService } from 'services/posthog-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_CREATION_NOTIF_ID } from 'shared/constants/notification-ids';
import { SMART_ASSISTANTS_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { MY_SMART_ASSISTANTS_FILTER } from 'shared/constants/smart-assistant';
import { SmartAssistant, SmartAssistantCreationProgress } from 'shared/interfaces/SmartAssistant';

export function useSmartAssistantCreationHandling() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const progressSubscription = useRef<Subscription>();

  const createSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.createSmartAssistant,
    onMutate: () => {
      navigate(ROUTE_LINKS.smartAssistants);
      NotificationService.startAction(SMART_ASSISTANT_CREATION_NOTIF_ID, 'FEED_BACK.SMART_ASSISTANT.LOADING.STARTING');
      progressSubscription.current = SmartAssistantService.smartAssistantCreationProgress.subscribe((progress) => {
        if (progress === SmartAssistantCreationProgress.UPLOADING_RESOURCES)
          NotificationService.startAction(
            SMART_ASSISTANT_CREATION_NOTIF_ID,
            'FEED_BACK.SMART_ASSISTANT.LOADING.UPLOADING_RESOURCES'
          );

        if (progress === SmartAssistantCreationProgress.CREATING_SMART_ASSISTANT)
          NotificationService.startAction(
            SMART_ASSISTANT_CREATION_NOTIF_ID,
            'FEED_BACK.SMART_ASSISTANT.LOADING.CREATE'
          );

        if (progress === SmartAssistantCreationProgress.UPLOADING_AVATAR)
          NotificationService.startAction(
            SMART_ASSISTANT_CREATION_NOTIF_ID,
            'FEED_BACK.SMART_ASSISTANT.LOADING.UPLOADING_AVATAR'
          );

        if (progress === SmartAssistantCreationProgress.CONFIGURING_TOOLS)
          NotificationService.startAction(
            SMART_ASSISTANT_CREATION_NOTIF_ID,
            'FEED_BACK.SMART_ASSISTANT.LOADING.CONFIGURING_TOOLS'
          );

        if (progress === SmartAssistantCreationProgress.DONE) progressSubscription.current?.unsubscribe();
      });
    },
    onSuccess: (newSmartAssistant: SmartAssistant) => {
      PosthogService.capture(PosthogEvent.SMART_ASSISTANT_CREATED, newSmartAssistant);
      NotificationService.endAction(
        true,
        SMART_ASSISTANT_CREATION_NOTIF_ID,
        'FEED_BACK.SMART_ASSISTANT.SUCCESS.CREATE'
      );
      queryClient.setQueryData(
        [SMART_ASSISTANTS_QUERY_KEY, MY_SMART_ASSISTANTS_FILTER],
        (existingSmartAssistants: SmartAssistant[] = []) => [newSmartAssistant, ...existingSmartAssistants]
      );
    },
    onError: (error: AxiosError) => {
      NotificationService.endAction(
        false,
        SMART_ASSISTANT_CREATION_NOTIF_ID,
        'FEED_BACK.SMART_ASSISTANT.FAILURE.CREATE',
        error
      );
      progressSubscription.current?.unsubscribe();
    }
  });

  return { createSmartAssistantMutation };
}
